/**
 * CompetitorsTab — Sprint 2.6
 *
 * Shows the CompetitorList (auto-suggested + manually added competitors)
 * and the TrackedDomainCard list (accepted competitors being actively tracked).
 * Also contains the Reach profile selector for this domain.
 */
import { useState, useEffect } from 'react';
import { api } from '../../api';
import type { TrackedDomainSummary, ReachProfile } from '../../types';
import { CompetitorList } from '../../components/CompetitorList';
import { TrackedDomainCard } from '../../components/TrackedDomainCard';

interface Props {
  domainId: string;
  tier: string;
}

const TIER_TRACKED_LIMITS: Record<string, number> = {
  starter: 5,
  growth:  15,
  scale:   50,
};

const REACH_LABELS: Record<ReachProfile, string> = {
  local:    'Local',
  national: 'National',
  global:   'Global',
};

const REACH_DESCRIPTIONS: Record<ReachProfile, string> = {
  local:    'City / metro level — location-anchored queries and LocalBusiness schema.',
  national: 'Country-wide — national keyword clusters, no city anchor.',
  global:   'Multi-country — hreflang recommendations, multi-market GEO sweeps.',
};

export function CompetitorsTab({ domainId, tier }: Props) {
  const [tracked, setTracked] = useState<TrackedDomainSummary[]>([]);
  const [reachProfile, setReachProfile] = useState<ReachProfile | null>(null);
  const [bcConfidence, setBcConfidence] = useState<number | null>(null);
  const [loadingTracked, setLoadingTracked] = useState(true);
  const [savingReach, setSavingReach] = useState(false);
  const [reachSaved, setReachSaved] = useState(false);
  const [error, setError] = useState('');

  const trackedLimit  = TIER_TRACKED_LIMITS[tier] ?? 5;
  const usedSlots     = tracked.length;
  const availableSlots = Math.max(0, trackedLimit - usedSlots);

  useEffect(() => {
    const domainReq  = api.domains.get(domainId);
    const trackedReq = api.reach.listTracked(domainId);
    const bcReq      = api.domains.getBusinessContext(domainId);

    Promise.allSettled([domainReq, trackedReq, bcReq]).then(([domainRes, trackedRes, bcRes]) => {
      if (domainRes.status === 'fulfilled') {
        setReachProfile(domainRes.value.reachProfile);
      }
      if (trackedRes.status === 'fulfilled') {
        setTracked(trackedRes.value);
      }
      if (bcRes.status === 'fulfilled' && bcRes.value) {
        setBcConfidence(bcRes.value.confidenceScore);
      }
      setLoadingTracked(false);
    });
  }, [domainId]);

  async function handleReachChange(profile: ReachProfile) {
    setSavingReach(true);
    setReachSaved(false);
    try {
      await api.reach.set(domainId, profile);
      setReachProfile(profile);
      setReachSaved(true);
      setTimeout(() => setReachSaved(false), 2000);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to update reach profile');
    } finally {
      setSavingReach(false);
    }
  }

  function handleTrackedRemoved(id: string) {
    setTracked((prev) => prev.filter((d) => d.id !== id));
  }

  return (
    <div className="space-y-8 py-2">

      {/* Reach profile section */}
      <section className="bg-white rounded-xl border border-gray-100 p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-semibold text-brand-ink text-sm">Reach profile</h3>
            <p className="text-xs text-gray-400 mt-0.5">Drives SERP scans, competitor discovery, and GEO citation queries.</p>
          </div>
          {reachSaved && <span className="text-xs text-brand-green">Saved</span>}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          {(Object.keys(REACH_LABELS) as ReachProfile[]).map((profile) => {
            const isGlobalLocked = profile === 'global' && (tier === 'starter' || tier === 'tier_1' || tier === 'free');
            const isSelected = reachProfile === profile;
            return (
              <button
                key={profile}
                type="button"
                disabled={savingReach || isGlobalLocked}
                onClick={() => !isGlobalLocked && void handleReachChange(profile)}
                className={`text-left rounded-lg border px-3 py-2.5 text-xs transition-colors ${
                  isGlobalLocked
                    ? 'border-gray-100 bg-gray-50 opacity-50 cursor-not-allowed'
                    : isSelected
                    ? 'border-brand-green bg-green-50'
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className={`font-semibold ${isSelected ? 'text-brand-green' : 'text-brand-ink'}`}>
                    {REACH_LABELS[profile]}
                  </span>
                  {isGlobalLocked && (
                    <span className="text-[10px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded">Growth+</span>
                  )}
                </div>
                <p className="text-gray-400 leading-relaxed">{REACH_DESCRIPTIONS[profile]}</p>
              </button>
            );
          })}
        </div>
        {error && <p className="text-xs text-red-500 mt-2">{error}</p>}
      </section>

      {/* Competitor suggestions + drag-reorder */}
      <section>
        <CompetitorList
          ownedDomainId={domainId}
          availableSlots={trackedLimit}
          businessContextConfidence={bcConfidence}
        />
      </section>

      {/* Tracked domains */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-brand-ink text-sm">Tracked</h3>
          <span className="text-xs text-gray-400">
            {usedSlots} / {trackedLimit} slots used
          </span>
        </div>

        {loadingTracked ? (
          <div className="flex justify-center py-6">
            <div className="w-5 h-5 rounded-full border-2 border-brand-green border-t-transparent animate-spin" />
          </div>
        ) : tracked.length === 0 ? (
          <div className="bg-white border border-gray-100 rounded-xl p-5 text-center text-sm text-gray-400">
            <p className="font-medium text-brand-ink mb-1">No tracked domains yet</p>
            <p>Accept competitor suggestions above to start tracking them.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {tracked.map((d) => (
              <TrackedDomainCard
                key={d.id}
                domain={d}
                onRemoved={handleTrackedRemoved}
              />
            ))}
          </div>
        )}
      </section>

    </div>
  );
}
